import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from './auth';
import { isUserBuild, type Build } from '@kilnci/shared';

interface PaginatedBuilds {
  items: Build[];
  total: number;
  page: number;
  pageSize: number;
}

export const useBuildsStore = defineStore('builds', () => {
  const builds = ref<Build[]>([]);
  const currentBuild = ref<Build | null>(null);
  const total = ref(0);
  const page = ref(1);
  const pageSize = ref(20);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const currentRepoFilter = ref<string | undefined>(undefined);
  const currentOrgFilter = ref<string | undefined>(undefined);
  const currentMineFilter = ref(false);

  const authStore = useAuthStore();

  async function fetchBuilds(params?: {
    page?: number;
    pageSize?: number;
    repo?: string;
    branch?: string;
    status?: string;
    organization?: string;
    mine?: boolean;
  }) {
    loading.value = true;
    error.value = null;

    currentRepoFilter.value = params?.repo;
    currentOrgFilter.value = params?.organization;
    currentMineFilter.value = params?.mine ?? false;

    try {
      const query = new URLSearchParams();
      if (params?.page) query.set('page', params.page.toString());
      if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
      if (params?.repo) query.set('repo', params.repo);
      if (params?.branch) query.set('branch', params.branch);
      if (params?.status) query.set('status', params.status);
      if (params?.organization) query.set('organization', params.organization);
      if (params?.mine) query.set('mine', 'true');

      const url = query.toString() ? `/api/builds?${query}` : '/api/builds';
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch builds');
      }

      const data: PaginatedBuilds = await response.json();
      builds.value = data.items;
      total.value = data.total;
      page.value = data.page;
      pageSize.value = data.pageSize;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
    } finally {
      loading.value = false;
    }
  }

  async function fetchBuild(id: string) {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/builds/${id}`, {
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch build');
      }

      currentBuild.value = await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
    } finally {
      loading.value = false;
    }
  }

  async function createBuild(request: {
    repositoryId: string;
    branch?: string;
    pipeline?: string;
    refType?: 'branch' | 'tag' | 'pull_request';
  }): Promise<Build | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch('/api/builds', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authStore.token}`,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create build');
      }

      const build: Build = await response.json();
      // Add to list at the beginning
      builds.value.unshift(build);
      return build;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function cancelBuild(id: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/builds/${id}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to cancel build');
      }

      // Update in list and current build. A running build is only
      // `cancelling` until its worker reports it stopped.
      const { build } = (await response.json()) as { build: Build };
      const index = builds.value.findIndex((b) => b.id === id);
      if (index !== -1) {
        builds.value[index].status = build.status;
      }
      if (currentBuild.value?.id === id) {
        currentBuild.value.status = build.status;
      }
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Patch fields on a build in-place across the list and currentBuild.
   * Undefined keys are left alone; `null` is a real value (e.g.
   * workerId: null means unassigned) and is applied. Used by the SSE
   * `build:updated` path (carries a full Build sans stages) and the
   * per-build WebSocket status stream (carries { id, status } only).
   *
   * Stages are deliberately excluded: they flow through
   * `build:stages-updated` / `updateBuildStages` so a coarse update
   * never clobbers a freshly-delivered stages array.
   */
  function applyBuildUpdate(patch: Partial<Build> & { id: string }) {
    const { id, stages: _stages, ...rest } = patch;
    const index = builds.value.findIndex((b) => b.id === id);
    if (index !== -1) {
      for (const [key, value] of Object.entries(rest)) {
        if (value !== undefined) {
          (builds.value[index] as Record<string, unknown>)[key] = value;
        }
      }
    }
    if (currentBuild.value?.id === id) {
      for (const [key, value] of Object.entries(rest)) {
        if (value !== undefined) {
          (currentBuild.value as Record<string, unknown>)[key] = value;
        }
      }
    }
  }

  function updateBuildStages(id: string, stages: Build['stages']) {
    const index = builds.value.findIndex((b) => b.id === id);
    if (index !== -1) {
      builds.value[index].stages = stages;
    }
    if (currentBuild.value?.id === id) {
      currentBuild.value.stages = stages;
    }
  }

  function addBuild(build: Build) {
    // Skip if build doesn't match current repo filter
    if (currentRepoFilter.value && build.repo !== currentRepoFilter.value) {
      return;
    }
    // Skip if build doesn't match current organization filter
    if (currentOrgFilter.value && build.organizationSlug !== currentOrgFilter.value) {
      return;
    }
    // Skip if build isn't the current user's work while filtering on it
    if (currentMineFilter.value && !isUserBuild(build, authStore.user?.email ?? '')) {
      return;
    }
    // Check if build already exists
    const exists = builds.value.some((b) => b.id === build.id);
    if (!exists) {
      // Add new build to the beginning of the list
      builds.value.unshift(build);
      // Update total count
      total.value++;
    }
  }

  return {
    builds,
    currentBuild,
    total,
    page,
    pageSize,
    loading,
    error,
    fetchBuilds,
    fetchBuild,
    createBuild,
    cancelBuild,
    applyBuildUpdate,
    updateBuildStages,
    addBuild,
  };
});
